import React, { createContext, useContext, useEffect, useState } from 'react';

import {
	useLoginMutation,
	useLogoutMutation,
	useRegisterMutation,
	useValidateTokenQuery,
} from '../api/auth';
import { paymentStatementApi } from '../api/base';
import { useAppDispatch } from '../store';
import {
	addBreadcrumb,
	clearUserContext,
	setUserContext,
} from '../utils/errorReporting';
import { clearSession, saveSession } from '../utils/session';

interface User {
	id: string;
	email: string;
	name: string;
	enterprise_id?: string;
	tenant_id?: string;
	roles?: string[];
	permissions?: string[];
}

interface AuthContextType {
	user: User | null;
	loading: boolean;
	login: (email: string, password: string) => Promise<void>;
	handleGoogleAuth: (authResult: any) => void;
	logout: () => void;
	register: (
		email: string,
		password: string,
		name: string,
		invitedEmail?: string,
	) => Promise<any>;
	isAuthenticated: boolean;
	isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error('useAuth must be used within a StandaloneAuthProvider');
	}
	return context;
};

interface AppAuthProviderProps {
	children: React.ReactNode;
}

export const AppAuthProvider: React.FC<AppAuthProviderProps> = ({
	children,
}) => {
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);
	const dispatch = useAppDispatch();

	const [loginMutation] = useLoginMutation();
	const [registerMutation] = useRegisterMutation();
	const [logoutMutation] = useLogoutMutation();

	const hasToken = !!localStorage.getItem('auth_token');

	const {
		data: tokenValidation,
		isLoading: validatingToken,
		error: validationError,
		refetch: refetchToken,
	} = useValidateTokenQuery(undefined, {
		skip: !hasToken,
		refetchOnMountOrArgChange: true,
	});

	useEffect(() => {
		if (hasToken) {
			// We have a token, wait for validation to complete
			if (!validatingToken) {
				// Validation completed (either success or error)
				if (tokenValidation && tokenValidation.success) {
					// Validation successful
					const userData = {
						id: tokenValidation.user_id,
						email: tokenValidation.email,
						name: tokenValidation.name,
						enterprise_id: tokenValidation.enterprise_id,
						tenant_id: tokenValidation.tenant_id,
						roles: tokenValidation.roles || [],
						permissions: tokenValidation.permissions || [],
					};
					setUser(userData);

					// Set user context for local error reporting hooks.
					setUserContext({
						id: userData.id,
						email: userData.email,
						username: userData.name,
					});

					addBreadcrumb(
						`User authenticated: ${userData.email}`,
						'auth',
						'info',
					);
					setLoading(false);
				} else if (
					validationError ||
					(tokenValidation && !tokenValidation.success)
				) {
					// Validation failed - clear token, user, and cache
					clearSession();
					setUser(null);

					// Reset RTK Query cache to clear any stale data from previous session
					dispatch(paymentStatementApi.util.resetApiState());

					clearUserContext();

					addBreadcrumb('User token validation failed', 'auth', 'warning');
					setLoading(false);
				} else if (tokenValidation === undefined && !validationError) {
					// Query hasn't completed yet - keep loading
					// This shouldn't happen if validatingToken is false, but handle it anyway
					return;
				}
			}
			// If validatingToken is true, keep loading state (don't set loading to false)
		} else {
			// No token - user is not authenticated
			setUser(null);
			setLoading(false);
		}
	}, [tokenValidation, validatingToken, validationError, hasToken, dispatch]);

	useEffect(() => {
		const token = localStorage.getItem('auth_token');
		if (token && !user && !loading) {
			refetchToken();
		}
	}, [user, loading, refetchToken]);

	const login = async (email: string, password: string) => {
		try {
			const result = await loginMutation({ email, password }).unwrap();

			if (result.success) {
				saveSession(result);
				if (result.user) {
					setUser(result.user);

					// Set user context for local error reporting hooks.
					setUserContext({
						id: result.user.id,
						email: result.user.email,
						username: result.user.name,
					});

					addBreadcrumb(`User logged in: ${result.user.email}`, 'auth', 'info');
				}
			} else {
				throw new Error(result.message || 'Login failed');
			}
		} catch (error: any) {
			console.error('Login error:', error);
			throw new Error(error.data?.message || error.message || 'Login failed');
		}
	};

	const register = async (
		email: string,
		password: string,
		name: string,
		invitedEmail?: string,
	) => {
		try {
			const result = await registerMutation({
				email,
				password,
				name,
				invited_email: invitedEmail,
			}).unwrap();

			if (result.success) {
				// If backend returned a token (invited signup), auto-login
				if ((result as any).token) {
					saveSession(result as any);
					if ((result as any).user) {
						setUser((result as any).user);
						setUserContext({
							id: (result as any).user.id,
							email: (result as any).user.email,
							username: (result as any).user.name,
						});
					}
				}
				return result;
			} else {
				throw new Error(result.message || 'Registration failed');
			}
		} catch (error: any) {
			console.error('Registration error:', error);
			throw new Error(
				error.data?.message || error.message || 'Registration failed',
			);
		}
	};

	const handleGoogleAuth = (authResult: any) => {
		try {
			if (authResult.success && authResult.token) {
				saveSession(authResult);

				if (authResult.user) {
					setUser(authResult.user);

					// Set user context for local error reporting hooks.
					setUserContext({
						id: authResult.user.id,
						email: authResult.user.email,
						username: authResult.user.name,
					});

					addBreadcrumb(
						`User logged in via Google OAuth: ${authResult.user.email}`,
						'auth',
						'info',
					);
				}

				console.log('Google OAuth successful, user authenticated');
			}
		} catch (error) {
			console.error('Google OAuth error:', error);
		}
	};

	const logout = async () => {
		try {
			await logoutMutation().unwrap();
		} catch (error) {
			console.error('Logout API error:', error);
		} finally {
			clearSession();
			setUser(null);

			// Reset RTK Query cache to clear previous user's data
			dispatch(paymentStatementApi.util.resetApiState());

			// Clear user context from local error reporting hooks.
			clearUserContext();

			addBreadcrumb('User logged out', 'auth', 'info');
		}
	};

	const value: AuthContextType = {
		user,
		loading: loading || (hasToken && validatingToken),
		login,
		handleGoogleAuth,
		logout,
		register,
		isAuthenticated: !!user,
		isAdmin:
			user?.roles?.some((role) =>
				['admin', 'enterprise_admin', 'super_admin'].includes(role),
			) || false,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AppAuthProvider;
